
import {
  defineComponent, PropType, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import { ShopsProducts } from '@/types/generated-types/graphql';
import InputText from 'primevue/inputtext';
import ShopsProductsList from '@/composables/shop-products/Shop-products';

const PAGE_SIZE = 50;

export default defineComponent({
  name: 'ProductSelection',

  components: {
    Dialog,
    Button,
    InputText,
  },

  props: {
    defaultLang: {
      type: String,
      required: true,
    },

    availableProductsArr: {
      type: Array as PropType<ShopsProducts[]>,
      required: true,
    },

    chosenProductsArr: {
      type: Array as PropType<ShopsProducts[]>,
      required: true,
    },

    productSearchStr: {
      type: String,
      required: true,
    },
  },

  emits: ['close-action-modal'],

  setup(props, context) {
    const { t } = useI18n();
    const dialogVisible = ref(true);
    const product = ref(props.productSearchStr);
    const productsAvailableList = ref<ShopsProducts[]>([...props.availableProductsArr]);
    const productsChosenList = ref<ShopsProducts[]>([...props.chosenProductsArr]);
    // Stocker les valeurs initiales pour annulation
    const initialChosenList = [...props.chosenProductsArr];
    const availableListRef = ref<HTMLElement | null>(null);

    const isLoading = ref(false);
    const isLoadingMore = ref(false);
    const hasMoreResults = ref(true);
    const currentOffset = ref(0);
    let searchTimer: ReturnType<typeof setTimeout> | null = null;
    let searchRequestId = 0; // Compteur pour ignorer les réponses obsolètes

    const handleCancel = () => {
      dialogVisible.value = false;
      // Retourner les valeurs initiales
      context.emit('close-action-modal', initialChosenList, props.availableProductsArr, props.productSearchStr);
    };

    const handleValidate = () => {
      dialogVisible.value = false;
      context.emit('close-action-modal', productsChosenList.value, productsAvailableList.value, product.value);
    };

    const updateDialogVisibility = (visible: boolean) => {
      if (!visible) {
        // Fermeture via croix = annulation
        handleCancel();
      }
    };

    const filterOutChosen = (products: ShopsProducts[]): ShopsProducts[] => products.filter(
      (p) => !productsChosenList.value.some((chosen) => chosen.id_shop_product === p.id_shop_product),
    );

    const handleSearch = async () => {
      if (searchTimer) clearTimeout(searchTimer);

      // Incrémenter immédiatement pour invalider les anciennes requêtes
      searchRequestId += 1;
      const currentRequestId = searchRequestId;

      // Ne pas rechercher si le champ est vide
      const searchTerm = product.value.trim();
      if (!searchTerm) {
        productsAvailableList.value = [];
        isLoading.value = false;
        return;
      }

      searchTimer = setTimeout(async () => {
        // Vérifier si une nouvelle recherche a été lancée pendant le debounce
        if (currentRequestId !== searchRequestId) return;

        isLoading.value = true;
        currentOffset.value = 0;
        hasMoreResults.value = true;
        productsAvailableList.value = [];

        const result = await ShopsProductsList(product.value, props.defaultLang, PAGE_SIZE, 0);

        // Ignorer si requête annulée ou nouvelle recherche lancée
        if (result === null || currentRequestId !== searchRequestId) return;

        productsAvailableList.value = filterOutChosen(result);
        hasMoreResults.value = result.length === PAGE_SIZE;
        currentOffset.value = PAGE_SIZE;
        isLoading.value = false;
      }, 500);
    };

    const loadMore = async () => {
      if (isLoadingMore.value || !hasMoreResults.value) return;

      searchRequestId += 1;
      const currentRequestId = searchRequestId;

      isLoadingMore.value = true;
      const result = await ShopsProductsList(product.value, props.defaultLang, PAGE_SIZE, currentOffset.value);

      // Ignorer si requête annulée ou nouvelle recherche lancée
      if (result === null || currentRequestId !== searchRequestId) {
        isLoadingMore.value = false;
        return;
      }

      if (result.length > 0) {
        const newProducts = filterOutChosen(result);
        productsAvailableList.value = [...productsAvailableList.value, ...newProducts];
        hasMoreResults.value = result.length === PAGE_SIZE;
        currentOffset.value += PAGE_SIZE;
      } else {
        hasMoreResults.value = false;
      }
      isLoadingMore.value = false;
    };

    const handleAvailableScroll = (event: Event) => {
      const target = event.target as HTMLElement;
      const { scrollTop, scrollHeight, clientHeight } = target;

      if (scrollHeight - scrollTop - clientHeight < 100) {
        loadMore();
      }
    };

    const selectItem = (productItem: ShopsProducts) => {
      productsChosenList.value.unshift(productItem);
      productsAvailableList.value = productsAvailableList.value.filter(
        (p) => p.id_shop_product !== productItem.id_shop_product,
      );
    };

    const removeItem = (productItem: ShopsProducts) => {
      productsChosenList.value = productsChosenList.value.filter(
        (p) => p.id_shop_product !== productItem.id_shop_product,
      );
    };

    const handleImageError = (event: Event) => {
      const img = event.target as HTMLImageElement;
      img.style.display = 'none';
      const parent = img.parentElement;
      if (parent) {
        const icon = document.createElement('i');
        icon.className = 'fa-thin fa-image fa-2x';
        parent.appendChild(icon);
      }
    };

    return {
      t,
      dialogVisible,
      updateDialogVisibility,
      handleCancel,
      handleValidate,
      product,
      handleSearch,
      productsAvailableList,
      productsChosenList,
      availableListRef,
      selectItem,
      removeItem,
      handleAvailableScroll,
      handleImageError,
      isLoading,
      isLoadingMore,
      hasMoreResults,
    };
  },
});
